import React, { useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { format } from "date-fns";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const styles = (theme) => ({
  container: {
    marginTop: "5.5rem",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "1.75rem",
  },
  filterContainer: {
    marginBottom: "1.5rem",
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
  },
  filterControl: {
    minWidth: "180px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.03)",
      "& fieldset": {
        borderColor: "rgba(232, 163, 61, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(232, 163, 61, 0.5)",
      },
    },
  },
  tableContainer: {
    backgroundColor: "#1C1E20",
    border: "1px solid rgba(232, 163, 61, 0.2)",
    borderRadius: "12px",
  },
  tableHeader: {
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    "& th": {
      color: "#ffffff",
      fontWeight: 700,
      borderBottom: "2px solid rgba(232, 163, 61, 0.3)",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.03)",
    },
    "& td": {
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      color: "rgba(255,255,255,0.9)",
    },
  },
  changePositive: {
    color: "#00C853",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  changeNegative: {
    color: "#F44336",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  dimensionChip: {
    fontSize: "0.75rem",
    height: "22px",
    margin: "0.1rem",
    backgroundColor: "rgba(232, 163, 61, 0.2)",
    color: "#E8A33D",
  },
  matchUpdateChip: {
    fontSize: "0.75rem",
    height: "22px",
    marginRight: "0.35rem",
    backgroundColor: "rgba(16, 185, 129, 0.18)",
    color: "#10B981",
  },
  reasonCell: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.35rem",
  },
  matchMeta: {
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.6)",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem",
    color: "rgba(255,255,255,0.6)",
  },
});

const dimensionLabels = {
  sportsmanship: "Sportsmanship",
  integrity: "Integrity",
  communication: "Communication",
  teamPlay: "Team Play",
  reliability: "Reliability",
  reporterQuality: "Reporter Quality",
};

function ReputationHistoryTable({ classes, history }) {
  const [filterDimension, setFilterDimension] = useState("all");

  // Filter history based on selected dimension
  const filteredHistory = history.filter((entry) => {
    if (filterDimension === "all") return true;
    return entry.dimensionsAffected?.includes(filterDimension);
  });

  return (
    <div className={classes.container}>
      <Typography className={classes.title}>Recent Reputation Changes</Typography>

      <div className={classes.filterContainer}>
        <FormControl className={classes.filterControl}>
          <InputLabel>Filter by Dimension</InputLabel>
          <Select
            value={filterDimension}
            label="Filter by Dimension"
            onChange={(e) => setFilterDimension(e.target.value)}
          >
            <MenuItem value="all">All Dimensions</MenuItem>
            <MenuItem value="sportsmanship">Sportsmanship</MenuItem>
            <MenuItem value="integrity">Integrity</MenuItem>
            <MenuItem value="communication">Communication</MenuItem>
            <MenuItem value="teamPlay">Team Play</MenuItem>
            <MenuItem value="reliability">Reliability</MenuItem>
            <MenuItem value="reporterQuality">Reporter Quality</MenuItem>
          </Select>
        </FormControl>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Dimensions Affected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className={classes.emptyState}>
                    <Typography>
                      {filterDimension === "all"
                        ? "No reputation changes yet"
                        : `No changes for ${dimensionLabels[filterDimension] || filterDimension}`}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredHistory.map((entry, index) => {
                const isPositive = entry.change > 0;
                const changeDisplay = isPositive ? `+${entry.change}` : entry.change;

                return (
                  <TableRow key={index} className={classes.tableRow}>
                    <TableCell>
                      {format(new Date(entry.timestamp), "MMM dd, yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Box
                        className={
                          isPositive ? classes.changePositive : classes.changeNegative
                        }
                      >
                        {isPositive ? (
                          <TrendingUpIcon fontSize="small" />
                        ) : (
                          <TrendingDownIcon fontSize="small" />
                        )}
                        {changeDisplay}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.reasonCell}>
                        {entry.source === "match_outcome" && (
                          <Chip
                            label="Match Update"
                            className={classes.matchUpdateChip}
                            size="small"
                          />
                        )}
                        <Typography component="span">{entry.reason}</Typography>
                        {entry.map && (
                          <Typography component="span" className={classes.matchMeta}>
                            {entry.map}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {entry.dimensionsAffected?.map((dim) => (
                        <Chip
                          key={dim}
                          label={dimensionLabels[dim] || dim}
                          className={classes.dimensionChip}
                          size="small"
                        />
                      )) || "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

ReputationHistoryTable.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      change: PropTypes.number.isRequired,
      reason: PropTypes.string.isRequired,
      dimensionsAffected: PropTypes.arrayOf(PropTypes.string),
      source: PropTypes.string,
      map: PropTypes.string,
      matchId: PropTypes.string,
    })
  ).isRequired,
};

ReputationHistoryTable.defaultProps = {
  history: [],
};

export default withStyles(styles)(ReputationHistoryTable);
